import useSWR from 'swr';

export enum SystemDowntimeState {
  Upcoming = 'upcoming',
  Active = 'active',
  Off = 'off',
}

interface SystemDowntime {
  state: SystemDowntimeState;
  title?: string;
  description?: string;
}

export const DEFAULT_SYSTEM_DOWNTIME: SystemDowntime = { state: SystemDowntimeState.Off };

const POLL_INTERVAL_MS = 60_000;

/** Anything but a valid upcoming/active body is off, so a bad upload can't lock users out. */
export const parseSystemDowntime = (json: unknown): SystemDowntime => {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return DEFAULT_SYSTEM_DOWNTIME;

  const { state, title, description } = json as Record<string, unknown>;

  if (state !== SystemDowntimeState.Upcoming && state !== SystemDowntimeState.Active) {
    return DEFAULT_SYSTEM_DOWNTIME;
  }

  return {
    state,
    title: typeof title === 'string' ? title : undefined,
    description: typeof description === 'string' ? description : undefined,
  };
};

// Throwing keeps SWR's last value; a missing file (S3 answers 403, not 404) is a real off.
const fetchSystemDowntime = async (url: string): Promise<SystemDowntime> => {
  const response = await fetch(url, { cache: 'no-store' });

  if (response.status === 403 || response.status === 404) return DEFAULT_SYSTEM_DOWNTIME;
  if (!response.ok) throw new Error(`System downtime feed returned ${response.status}`);

  try {
    return parseSystemDowntime(await response.json());
  } catch {
    return DEFAULT_SYSTEM_DOWNTIME;
  }
};

export const useSystemDowntime = (): null | SystemDowntime => {
  // SWR never fetches during SSR, and its focus revalidation also fires on visibility regain.
  const { data } = useSWR(process.env.systemDowntimeUrl || null, fetchSystemDowntime, {
    refreshInterval: POLL_INTERVAL_MS,
    refreshWhenHidden: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: false,
  });

  return data ?? null;
};
